import {FC, lazy, LazyExoticComponent, ReactNode, Suspense, useMemo, useState} from "react";
import {BaseTemplateProps, ScreenType, TemplatePropsMap} from "../../types/templates.ts";
import {useTemplate} from "../../contexts/templateContext.tsx";
import {getTemplateComponentPath} from "@/utils/get-template-component-name.ts";

type DynamicTemplateLoaderProps<T extends ScreenType> = {
    screenType: T;
    children?: ReactNode;
    componentProps: TemplatePropsMap[T]
}

type LazyTemplateType = LazyExoticComponent<FC<BaseTemplateProps>> | null;

export const DynamicTemplateLoader = <T extends ScreenType>( {
        children,
        screenType,
        componentProps,
    }: DynamicTemplateLoaderProps<T> ) => {
    const { template } = useTemplate();

    const [LazyTemplateComponent, setLazyTemplateComponent] = useState<LazyTemplateType>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    useMemo(() => {
        let isMounted = true;
        setLoadError(null);

        if (template) {
            console.log(`Loading template ${template}: ${screenType}`);

            try {
                const module = getTemplateComponentPath({
                    template,
                    screenType,
                });

                const NewLazyComponent = lazy(module);

                if (isMounted) {
                    setLazyTemplateComponent(() => NewLazyComponent as LazyExoticComponent<FC<BaseTemplateProps>>);
                }
            }
            catch (error) {
                console.error(`Error while loading dynamic template ${template}`, error);
                if (isMounted) {
                    setLoadError(`Cannot load template ${template}`);
                    setLazyTemplateComponent(null);
                }
            }
        } else {
            if (isMounted) {
                setLazyTemplateComponent(null);
            }
            console.log("Template name is empty!");
        }

        return () => {
            isMounted = false;
        };
    }, [template]);

    return (
        <Suspense fallback={<p>Ładowanie...</p>}>
            {LazyTemplateComponent && !loadError ? (
                <LazyTemplateComponent {...componentProps}>
                    {children}
                </LazyTemplateComponent>
                ) : !LazyTemplateComponent && !loadError ? (
                    <p>Szablon nie jest wybrany!</p>
                ) : null}
        </Suspense>
    );
}